import {
  AbsoluteBearing,
  Bearing,
  BearingType,
  Component,
  GeneratableBearing,
  LineLocation,
  RelativeBearingGeometry,
  RelativeBearingsGeometry,
  RelativeContinuousBearingGeometry,
} from '@paragon/contract-library';
import { Direction2D, distanceEquals, Line2D, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { findAndCalculateLine } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch } from '@paragon/utilities';

export interface BearingVisualData {
  position: Point2D;
  width: number;
  height: number;
  normal: Direction2D;
}

function getBearingHeight(bearing: { readonly bearingType: BearingType }, bearingWidth: number) {
  // 3 comes from 1.5" * 2 (2 2x4s for a double plate bearing).
  // This works fine for other bearing types besides hangers too.
  const defaultBearingHeight = 3;

  return bearing.bearingType === 'Hanger' ? Math.max(bearingWidth * 2, defaultBearingHeight) : defaultBearingHeight;
}

export const calculateBearingVisualData = (bearing: Bearing, component: Component): BearingVisualData | null => {
  const bearingGeometry = calculateBearingGeometry(bearing, component);
  if (!bearingGeometry) return null;
  return {
    ...bearingGeometry,
    height: getBearingHeight(bearing, bearingGeometry.width),
  };
};

/**
 * This only calculates a placeholder geometry for generatable bearings, so the user has some feedback during profile editing.
 */
export const calculateGeneratableBearingVisualData = (
  bearing: GeneratableBearing,
  span: number,
): BearingVisualData | null => {
  const anchorX = bearing.offsetSide === 'Left' ? bearing.offset : span - bearing.offset;
  if (bearing.generatableBearingType === 'VerticalGeneratableBearing') {
    // We can't know the location of a top chord vertical bearing until the chord has been generated.
    if (bearing.chordLocation === 'Top') return null;
    return {
      position: new Point2D(anchorX, bearing.width / 2),
      width: bearing.width,
      normal: anchorX > span / 2 ? Direction2D.left : Direction2D.right,
      height: getBearingHeight(bearing, bearing.width),
    };
  }
  const widthCoefficient = bearing.anchor === 'Left' ? 0.5 : bearing.anchor === 'Right' ? -0.5 : 0;
  return {
    position: new Point2D(anchorX + widthCoefficient * bearing.width, 0),
    width: bearing.width,
    normal: Direction2D.up,
    height: getBearingHeight(bearing, bearing.width),
  };
};

export interface BearingGeometry {
  position: Point2D;
  width: number;
  normal: Direction2D;
}

export const calculateBearingGeometry = (bearing: Bearing, component: Component): BearingGeometry | null =>
  bearing.relativeGeometry
    ? calculateRelativeBearingGeometry(bearing.relativeGeometry, component)
    : calculateAbsoluteBearingGeometry(bearing);

export const calculateAbsoluteBearingGeometry = (bearing: AbsoluteBearing): BearingGeometry => {
  const position = new Point2D(bearing.centerPointX, bearing.centerPointY);
  const normal = Direction2D.fromObject(bearing.normal);
  return { position, width: bearing.width, normal };
};

export const calculateRelativeBearingGeometry = (
  relativeGeometry: RelativeBearingsGeometry,
  component: Component,
): BearingGeometry | null => {
  if (relativeGeometry.bearingGeometryType === 'Bearing') {
    return calculateRegularBearingGeometry(relativeGeometry, component);
  }
  return calculateContinuousBearingGeometry(relativeGeometry, component);
};

const calculateRegularBearingGeometry = (
  relativeGeometry: RelativeBearingGeometry,
  component: Component,
): BearingGeometry | null => {
  const bearingLine = findAndCalculateLine(relativeGeometry.bearingLineGuid, component);
  const definitionLine = findAndCalculateLine(relativeGeometry.lineGuid, component);
  if (!bearingLine || !definitionLine) return null;

  const intersection = intersectWithBearingLine(bearingLine, definitionLine);
  if (!intersection) return null;
  const offset = getOffsetToCenter(relativeGeometry.lineLocation, relativeGeometry.width);
  const centerPosition = intersection.translate(bearingLine.direction.toVector(offset));

  const normal = relativeGeometry.directionReversed
    ? bearingLine.direction.rotateReverseQuarterTurn()
    : bearingLine.direction.rotateQuarterTurn();

  return { position: centerPosition, width: relativeGeometry.width, normal };
};

const calculateContinuousBearingGeometry = (
  relativeGeometry: RelativeContinuousBearingGeometry,
  component: Component,
): BearingGeometry | null => {
  const bearingLine = findAndCalculateLine(relativeGeometry.bearingLineGuid, component);
  const leftSideLine = findAndCalculateLine(relativeGeometry.leftSideLineGuid, component);
  const rightSideLine = findAndCalculateLine(relativeGeometry.rightSideLineGuid, component);
  if (!bearingLine || !leftSideLine || !rightSideLine) return null;

  const backTopLeftBearingPoint = intersectWithBearingLine(bearingLine, leftSideLine);
  const backTopRightBearingPoint = intersectWithBearingLine(bearingLine, rightSideLine);
  if (!backTopLeftBearingPoint || !backTopRightBearingPoint) return null;
  // This calculation assumes the bearing is horizontal.
  const width = backTopLeftBearingPoint.distanceTo(backTopRightBearingPoint);
  if (distanceEquals(width, 0)) return null;
  const centerPosition = new LineSegment2D(backTopLeftBearingPoint, backTopRightBearingPoint).midpoint;

  const normal = relativeGeometry.directionReversed
    ? bearingLine.direction.rotateReverseQuarterTurn()
    : bearingLine.direction.rotateQuarterTurn();

  return { position: centerPosition, width, normal };
};

export function calculateBearingSegment(geometry: BearingGeometry): LineSegment2D {
  const direction = geometry.normal.rotateQuarterTurn();

  return new LineSegment2D(
    geometry.position.translate(direction.toVector(geometry.width / 2)),
    geometry.position.translate(direction.reverse().toVector(geometry.width / 2)),
  );
}

const intersectWithBearingLine = (bearingLine: Line2D, otherLine: Line2D) => {
  const intersection = bearingLine.intersectWithLine(otherLine);
  if (!intersection) {
    console.warn('Bearing definition lines did not intersect.');
  }
  return intersection;
};

const getOffsetToCenter = (from: LineLocation, width: number) => {
  switch (from) {
    case 'Starboard':
      return width / 2;
    case 'Center':
      return 0;
    case 'Port':
      return -width / 2;
    default:
      assertExhaustiveSwitch(from, 'LineLocation type unknown.');
  }
};
